// src/components/Scrutineer.js - Scrutineer Interface (View Only)
import React, { useState, useEffect } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { getDancersList } from '../utils/fieldCompatibility';
import './ChairAdjudicator.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const ROUND_TYPES = [
  'TBD',
  'Round 1',
  'Round 2',
  'Round 3',
  'Round 4',
  'Round 5',
  'Round 6',
  'Round 7',
  'Round 8',
  'Quarter-Final',
  'Semi-Final',
  'Final'
];

// Dancers Modal Component
function DancersModal({ event, onClose, dancers, onRemoveDancer }) {
  const [clubs, setClubs] = useState([]);

  // Fetch clubs data for location resolution
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const response = await fetch(`https://${_host}:6003/api/${_company}/clubs`);
        if (response.ok) {
          const clubsData = await response.json();
          setClubs(clubsData);
        }
      } catch (error) {
        console.error('Error fetching clubs:', error);
      }
    };

    fetchClubs();
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Dancers in Event</h3>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {dancers && dancers.length > 0 ? (
            <div className="modal-dancers-list">
              <div className="dancers-header">
                <span className="dancers-count-header">
                  {dancers.length} Dancer{dancers.length !== 1 ? 's' : ''}
                </span>
              </div>
              {dancers.map((dancer, index) => (
                <DancerModalItem
                  key={index}
                  dancer={dancer}
                  clubs={clubs}
                  onRemoveDancer={onRemoveDancer}
                />
              ))}
            </div>
          ) : (
            <div className="modal-empty-message">
              <p>No dancers allocated to this event</p>
              <span className="modal-hint">Dancers need to be allocated from the Administration section</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual dancer item component for the modal
function DancerModalItem({ dancer, clubs, onRemoveDancer }) {
  const [dancerDetails, setDancerDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch complete dancer information including club details
  useEffect(() => {
    const fetchDancerDetails = async () => {
      if (!dancer.coupleId && !dancer.cloth_number && !dancer.clothNumber) {
        setLoading(false);
        return;
      }

      try {
        // Try to get dancer details using cloth number
        const clothNumber = dancer.cloth_number || dancer.clothNumber;
        if (clothNumber) {
          const response = await fetch(`https://${_host}:6003/api/${_company}/couples/by-cloth-number/${clothNumber}`);
          if (response.ok) {
            const details = await response.json();
            setDancerDetails(details);
          }
        }
      } catch (error) {
        console.error('Error fetching dancer details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDancerDetails();
  }, [dancer.coupleId, dancer.cloth_number, dancer.clothNumber]);

  const getDancerName = () => {
    // Use fetched details if available, otherwise fall back to event dancer data
    if (dancerDetails) {
      if (dancerDetails.dancerType === 'solo') {
        return dancerDetails.name || 'Unknown Solo Dancer';
      } else if (dancerDetails.dancerType === 'team') {
        return dancerDetails.name || `${dancerDetails.teamMembers?.join(' & ') || 'Unknown Team'}`;
      } else {
        // Couple
        const lead = dancerDetails.leadName;
        const follow = dancerDetails.followName;
        if (lead && follow) {
          return `${lead} & ${follow}`;
        } else if (lead) {
          return lead;
        } else if (follow) {
          return follow;
        }
      }
    }

    // Fallback to event dancer data
    return dancer.name || dancer.displayName || 'Unknown Dancer';
  };

  const getDancerType = () => {
    // Use fetched details if available, otherwise fall back to event dancer data
    const dancerType = dancerDetails?.dancerType || dancer.dancerType || dancer.dancer_type || 'Couple';
    return dancerType.charAt(0).toUpperCase() + dancerType.slice(1);
  };

  const getClubInfo = () => {
    // Try to get club info from fetched dancer details
    if (dancerDetails?.clubId || dancerDetails?.club_id) {
      const clubId = dancerDetails.clubId || dancerDetails.club_id;
      const club = clubs.find(c => (c.id === clubId || c.club_id === clubId));
      if (club) {
        return {
          name: club.name || club.club_name,
          country: club.country
        };
      }
    }

    // Fallback to dancer data from event
    if (dancer.clubId || dancer.club_id) {
      const clubId = dancer.clubId || dancer.club_id;
      const club = clubs.find(c => (c.id === clubId || c.club_id === clubId));
      if (club) {
        return {
          name: club.name || club.club_name,
          country: club.country
        };
      }
    }

    return null;
  };

  const getLocationInfo = () => {
    // Priority order: club country -> dancer country -> club name -> location unknown
    const clubInfo = getClubInfo();

    // Priority 1: Club country (if available)
    if (clubInfo?.country) {
      return <span className="modal-location">{clubInfo.country}</span>;
    }

    // Priority 2: Dancer country from details
    if (dancerDetails?.country) {
      return <span className="modal-location">{dancerDetails.country}</span>;
    }

    // Priority 3: Club name (as location fallback)
    if (clubInfo?.name) {
      return <span className="modal-location">{clubInfo.name}</span>;
    }

    // Priority 4: Club name from dancer data
    if (dancer.clubName || dancer.club_name) {
      return <span className="modal-location">{dancer.clubName || dancer.club_name}</span>;
    }

    // Fall back to event dancer data (priority 2)
    if (dancer.country) {
      return <span className="modal-location">{dancer.country}</span>;
    }

    return <span className="modal-location">Location Unknown</span>;
  };

  // Get registration check-in status
  const getRegistrationStatus = () => {
    if (loading) {
      return <span className="status-loading">Loading...</span>;
    }

    // Use dancerDetails if available, otherwise fall back to event dancer data
    const regCheckin = dancerDetails?.registrationCheckin !== undefined
      ? dancerDetails.registrationCheckin
      : dancerDetails?.registration_checkin !== undefined
      ? dancerDetails.registration_checkin
      : dancer?.registrationCheckin
      || dancer?.registration_checkin;

    return (
      <span className={`checkin-status ${regCheckin === true ? 'checked-in' : 'not-checked-in'}`}>
        {regCheckin === true ? '✓ PRESENT' : '✗ NOT ARRIVED'}
      </span>
    );
  };

  // Get marshall check-in status
  const getMarshallStatus = () => {
    if (loading) {
      return <span className="status-loading">Loading...</span>;
    }

    // Use dancerDetails if available, otherwise fall back to event dancer data
    const marshallCheckin = dancerDetails?.marshallCheckin !== undefined
      ? dancerDetails.marshallCheckin
      : dancerDetails?.marshall_checkin !== undefined
      ? dancerDetails.marshall_checkin
      : dancer?.marshallCheckin
      || dancer?.marshall_checkin;

    return (
      <span className={`checkin-status ${marshallCheckin === true ? 'checked-in' : 'not-checked-in'}`}>
        {marshallCheckin === true ? '✓ READY' : '✗ NOT MARSHALLED'}
      </span>
    );
  };

  return (
    <div className="modal-dancer-item">
      <div className="modal-dancer-number">{dancer.cloth_number || dancer.clothNumber}</div>
      <div className="modal-dancer-info">
        <div className="modal-dancer-name">{getDancerName()}</div>
        <div className="modal-dancer-details">
          <span className="modal-dancer-type">{getDancerType()}</span>
          <span className="modal-location-separator">•</span>
          {getLocationInfo()}
        </div>
        <div className="modal-dancer-checkin-status">
          {getRegistrationStatus()}
          <span className="status-separator">•</span>
          {getMarshallStatus()}
        </div>
      </div>
      <div className="modal-dancer-actions">
        <button
          className="modal-remove-dancer-btn"
          onClick={() => onRemoveDancer(dancer)}
          title="Remove dancer from event"
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

function Scrutineer() {
  const { selectedCompetition } = useCompetition();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDancersModal, setShowDancersModal] = useState(false);
  const [selectedEventForDancers, setSelectedEventForDancers] = useState(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [dancerToRemove, setDancerToRemove] = useState(null);

  // Fetch events from API
  const fetchEvents = async (isInitial = false) => {
    try {
      // Only set loading to true on initial fetch, not on polling
      if (isInitial) {
        setLoading(true);
      }
      let response;

      if (selectedCompetition) {
        response = await fetch(`https://${_host}:6003/api/${_company}/events?competitionId=${selectedCompetition.id}`);
      } else {
        response = await fetch(`https://${_host}:6003/api/${_company}/events`);
      }

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();

      // Filter events locally if needed
      const filteredEvents = selectedCompetition
        ? data.filter(event => event.competitionId === selectedCompetition.id)
        : data;

      setEvents(prevEvents => {
        // Only update if events actually changed to prevent unnecessary re-renders
        const prevIds = prevEvents.map(e => e.id).sort();
        const newIds = filteredEvents.map(e => e.id).sort();

        if (JSON.stringify(prevIds) !== JSON.stringify(newIds)) {
          return filteredEvents;
        }
        return prevEvents;
      });

      if (isInitial) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      if (isInitial) {
        setLoading(false);
      }
    }
  };

  // Fetch events when component mounts or when competition changes
  useEffect(() => {
    if (selectedCompetition) {
      fetchEvents(true); // Initial fetch
      // Set up polling to refresh events periodically (without loading state)
      const interval = setInterval(() => fetchEvents(false), 5000); // Poll every 5 seconds

      // Clean up interval on component unmount
      return () => clearInterval(interval);
    }
  }, [selectedCompetition]);

  const getDancerCount = (event) => {
    const dancers = getDancersList(event);
    return dancers ? dancers.length : 0;
  };

  const handleItemNumberClick = (event) => {
    setSelectedEventForDancers(event);
    setShowDancersModal(true);
  };

  const handleCloseModal = () => {
    setShowDancersModal(false);
    setSelectedEventForDancers(null);
  };

  const handleRemoveDancer = (dancer) => {
    setDancerToRemove(dancer);
    setShowRemoveConfirmation(true);
  };

  const confirmRemoveDancer = async () => {
    if (!dancerToRemove || !selectedEventForDancers) return;

    try {
      // Get the current dancers list from the event
      const currentDancers = getDancersList(selectedEventForDancers);

      // Remove the dancer from the list
      const updatedDancers = currentDancers.filter(d =>
        (d.cloth_number || d.clothNumber) !== (dancerToRemove.cloth_number || dancerToRemove.clothNumber)
      );

      // Update the event with the new dancers list
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${selectedEventForDancers.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...selectedEventForDancers,
          dancers: updatedDancers
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove dancer from event');
      }

      // Update local state
      const updatedEvents = events.map(event =>
        event.id === selectedEventForDancers.id
          ? { ...event, dancers: updatedDancers }
          : event
      );
      setEvents(updatedEvents);

      // Update the selected event with the new dancers list to refresh the modal
      setSelectedEventForDancers(prevEvent => ({
        ...prevEvent,
        dancers: updatedDancers
      }));

      // Show success message
      setSuccess('Dancer removed from event successfully');
      setTimeout(() => setSuccess(null), 3000);

    } catch (error) {
      console.error('Error removing dancer:', error);
      setError('Failed to remove dancer from event');
      setTimeout(() => setError(null), 3000);
    } finally {
      // Close confirmation dialog
      setShowRemoveConfirmation(false);
      setDancerToRemove(null);
    }
  };

  const cancelRemoveDancer = () => {
    setShowRemoveConfirmation(false);
    setDancerToRemove(null);
  };

  // Sort events by item number (ascending)
  const sortedEvents = [...events].sort((a, b) => {
    const itemA = parseInt(a.itemNumber) || 999;
    const itemB = parseInt(b.itemNumber) || 999;
    return itemA - itemB;
  });

  // Filter events based on search query
  const filteredEvents = sortedEvents.filter(event => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();

    // Search by item number
    const itemNumber = (event.itemNumber || event.item_number || '').toString();
    if (itemNumber.includes(query)) return true;

    // Search by event name
    if (event.name && event.name.toLowerCase().includes(query)) return true;

    return false;
  });

  return (
    <div className="scrutineer-container">
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="page-header">
          <h1>Scrutineer</h1>
          {selectedCompetition && (
            <p className="page-subtitle">{selectedCompetition.name}</p>
          )}
        </div>

        {/* Search Bar */}
        <div className="search-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      <div className="scrutineer-content">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {loading ? (
          <div className="loading">Loading events...</div>
        ) : filteredEvents.length === 0 ? (
          <div className="no-events">
            {searchQuery ? 'No events match your search.' : 'No events found for this competition.'}
          </div>
        ) : (
          <div className="events-table-wrapper">
            <table className="events-table">
              <thead>
                <tr>
                  <th width="8%">Item #</th>
                  <th width="35%">Event Name</th>
                  <th width="15%">Round Type</th>
                  <th width="8%">Heats</th>
                  <th width="10%">Dancers</th>
                  <th width="12%">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <tr key={event.id}>
                    <td data-label="Item #">
                      <span
                        className="item-number-badge clickable"
                        onClick={() => handleItemNumberClick(event)}
                        title="Click to view dancers"
                      >
                        {event.itemNumber || '-'}
                      </span>
                    </td>
                    <td data-label="Event Name">
                      <div className="event-name-cell">
                        <div className="event-name">{event.name}</div>
                        <div className="event-meta">
                          {event.dances?.length || 0} dances
                        </div>
                      </div>
                    </td>
                    <td data-label="Round Type">
                      <span className={`round-badge ${event.roundType === 'TBD' ? 'tbd' : ''}`}>
                        {event.roundType || 'TBD'}
                      </span>
                    </td>
                    <td data-label="Heats">
                      <span className="heats-display">{event.numHeats || 1}</span>
                    </td>
                    <td data-label="Dancers">
                      <span className="dancers-count">
                        {getDancerCount(event)}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge ${event.broadcast_state === 'live' ? 'ready' : 'pending'}`}>
                        {event.broadcast_state === 'live' ? 'Ready' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredEvents.length === 0 && searchQuery && (
              <div className="no-search-results">
                No events found matching "{searchQuery}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dancers Modal */}
      {showDancersModal && selectedEventForDancers && (
        <DancersModal
          event={selectedEventForDancers}
          onClose={handleCloseModal}
          dancers={getDancersList(selectedEventForDancers)}
          onRemoveDancer={handleRemoveDancer}
        />
      )}

      {/* Remove Dancer Confirmation Dialog */}
      {showRemoveConfirmation && dancerToRemove && (
        <div className="modal-overlay" onClick={cancelRemoveDancer}>
          <div className="modal-content confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Removal</h3>
              <button className="modal-close-btn" onClick={cancelRemoveDancer}>×</button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to remove dancer
                <strong> {dancerToRemove.cloth_number || dancerToRemove.clothNumber}</strong>
                {' '}from event
                <strong> {selectedEventForDancers?.name}</strong>?
              </p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={cancelRemoveDancer}
              >
                Cancel
              </button>
              <button
                className="confirm-remove-btn"
                onClick={confirmRemoveDancer}
              >
                Remove Dancer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Scrutineer;